import { type ReactElement } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { useOpsEvalsData } from './OpsEvalsContext'
import { EXECUTION_LABEL } from './types'

/**
 * The run in flight, or why the last one failed. The new-run dialog closes when a run
 * starts, so this is the only place that shows a run is still going, and that a reload
 * would stop it.
 */
export function RunProgressBanner(): ReactElement | null {
  const { progress, runError, dismissRunError } = useOpsEvalsData()

  if (progress) {
    const share = progress.total ? progress.done / progress.total : 0
    return (
      <section
        aria-live="polite"
        className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="font-medium">Running {progress.title}</span>
          <span className="tabular-nums">
            {progress.done} of {progress.total} calls
            {progress.models.length > 1 && ` · now on ${progress.model}`}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label="Run progress"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.done}
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-100"
        >
          <div className="h-full rounded-full bg-blue-600 transition-[width]" style={{ width: `${share * 100}%` }} />
        </div>
        <p className="mt-2 text-caption-sm">
          {EXECUTION_LABEL[progress.execution]} · {progress.models.join(`, `)}. Keep this tab open: reloading it, or a
          code change on the dev server, stops the run. It opens here when it finishes.
        </p>
      </section>
    )
  }

  if (runError) {
    return (
      <section
        role="alert"
        className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
      >
        <span className="flex items-start gap-2">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>The last run failed: {runError}</span>
        </span>
        <Button variant="tertiary" size="xs" onClick={dismissRunError}>
          Dismiss
        </Button>
      </section>
    )
  }

  return null
}
